import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2 } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

export default function HouseholdReviews() {
    const [rows, setRows] = useState([]);
    const [dir, setDir]   = useState("in");

    const load = async () => {
        const r = await api.get("/household/reviews", { params: { direction: dir } }).catch(() => null);
        setRows(r?.data?.reviews || []);
    };
    useEffect(() => { load(); }, [dir]);

    const complete = async (id) => {
        try { await api.post(`/household/reviews/${id}/complete`); toast.success("Marked complete"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Couldn't complete"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="household-reviews-page">
            <HouseholdNav />
            <div className="mb-4">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <MessageSquare size={12} /> Review requests
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                    Ask a household member to take a second look.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Send a privacy-safe review request for an expense, payment, subscription, document,
                    goal or task. Notifications never expose details when the record&rsquo;s privacy is
                    restricted.
                </p>
            </div>

            <div className="flex gap-1 border border-stone-200 rounded-full p-1 bg-white w-fit mb-4">
                {[{ k: "in", l: "Incoming" }, { k: "out", l: "Sent by me" }].map(o => (
                    <button key={o.k} onClick={() => setDir(o.k)}
                            className={`px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${dir === o.k ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                            data-testid={`reviews-dir-${o.k}`}>
                        {o.l}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {rows.map(r => (
                    <div key={r.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`review-${r.id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">
                                {r.kind}
                            </span>
                            <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${r.status === "Completed" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                                {r.status}
                            </span>
                            <span className="text-[10.5px] text-stone-500 ml-auto">{(r.created_at || "").slice(0, 10)}</span>
                        </div>
                        <div className="text-[14px] font-semibold text-stone-900">
                            {r.from_name} asked to review a household item
                        </div>
                        {(r.visibility === "Shared Full" || r.visibility === "Shared Summary") && r.record_ref?.label && (
                            <div className="text-[12.5px] text-stone-700 mt-1">Regarding: {r.record_ref.label}</div>
                        )}
                        {r.message && <div className="text-[12px] text-stone-500 mt-1.5 italic">&ldquo;{r.message}&rdquo;</div>}
                        {dir === "in" && r.status === "Open" && (
                            <button onClick={() => complete(r.id)}
                                    className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-800 border border-emerald-200 rounded-full px-3 py-1"
                                    data-testid={`review-complete-${r.id}`}>
                                <CheckCircle2 size={11} /> Mark reviewed
                            </button>
                        )}
                    </div>
                ))}
                {rows.length === 0 && (
                    <div className="text-center py-16 text-stone-500 text-[13px]" data-testid="reviews-empty">
                        No review requests {dir === "in" ? "waiting for you" : "sent by you"}.
                    </div>
                )}
            </div>
        </div>
    );
}
